/**
 * Everything the sim promises to the outside world.
 *
 * The renderer, the UI and the replay recorder all read `SimSnapshot` and
 * nothing else — no Rapier handles leak out. That keeps the sim swappable and,
 * more importantly, makes replays a pure function of recorded snapshots.
 */

export interface RaidInput {
  /** World-space intent, already camera-relative, each in -1..1. */
  moveX: number
  moveZ: number
  jump: boolean
}

export const NEUTRAL_INPUT: RaidInput = { moveX: 0, moveZ: 0, jump: false }

export type RaidEventType =
  | 'start'
  /** Fired by a spring board. The signature ToyRaiders moment. */
  | 'launched'
  /** Clipped by a swatter arm. */
  | 'swatted'
  /** Blown off course by a fan. */
  | 'pushed'
  /** Stepped onto marbles and lost the brakes. */
  | 'slipped'
  /** Stuck in glue — pays in escape time. */
  | 'glued'
  /** Fell in a hole, sent back to the door. */
  | 'pitfall'
  /** Walked up to a fake chest. Pure comedy for the owner. */
  | 'decoy'
  /** Stepped on a bluff plate — the owner learns their theatre worked. */
  | 'bluffed'
  /** Picked up the real chest; the escape clock starts. */
  | 'pickup'
  | 'escaped'
  /** Ran out of raid time, or the door locked while carrying. */
  | 'failed'
  /** Stood still long enough to be visibly unsure. The owner's favourite datum. */
  | 'hesitate'

export interface RaidEvent {
  /** Seconds since raid start. */
  t: number
  type: RaidEventType
  x: number
  z: number
  /** Prop instance responsible, when there is one. */
  iid?: number
}

export type RaidPhase = 'sneaking' | 'carrying' | 'escaped' | 'failed'

export type RaiderPose = 'stand' | 'run' | 'air' | 'flung' | 'stuck'

export interface RaiderState {
  x: number
  y: number
  z: number
  /** Facing, radians. */
  yaw: number
  /** Visual tumble angle while flung — cheap ragdoll comedy without a ragdoll. */
  tumble: number
  vx: number
  vy: number
  vz: number
  speed: number
  grounded: boolean
  pose: RaiderPose
  carrying: boolean
}

export interface SimSnapshot {
  time: number
  phase: RaidPhase
  raider: RaiderState
  /** Seconds left before the door locks. Only meaningful while carrying. */
  escapeRemaining: number
  raidRemaining: number
  /** Cell the raider currently stands in. */
  cellX: number
  cellZ: number
  /** Props that have fired at least once, for "已暴露" rendering. */
  firedIids: readonly number[]
}
